/**
 * PurchaseStatusHandler Component
 * Handles Stripe checkout return flow (success/cancel)
 * Displays appropriate messages and refreshes credit balance
 */

import { useEffect, useState, useCallback } from 'react';
import { useCredits } from '../../hooks/useCredits';

/**
 * PurchaseStatusHandler component
 * Monitors URL query parameters for purchase status
 * Shows success/cancel messages and refreshes credits
 */
export function PurchaseStatusHandler() {
  const [status, setStatus] = useState<'success' | 'cancelled' | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const { refreshCredits } = useCredits();

  /**
   * Check URL for purchase status on mount
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchaseStatus = params.get('purchase');
    const stripeSessionId = params.get('session_id');

    if (purchaseStatus === 'success') {
      setStatus('success');
      setSessionId(stripeSessionId);
      
      // Refresh credits after successful purchase
      refreshCredits().catch((error) => {
        console.error('[PurchaseStatusHandler] Failed to refresh credits:', error);
      });

      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } else if (purchaseStatus === 'cancelled') {
      setStatus('cancelled');

      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [refreshCredits]);

  /**
   * Close the status message
   */
  const handleClose = useCallback(() => {
    setStatus(null);
    setSessionId(null);
  }, []);

  /**
   * Auto-close success message after 10 seconds
   */
  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        handleClose();
      }, 10000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [status, handleClose]);

  /**
   * Auto-close cancel message after 7 seconds
   */
  useEffect(() => {
    if (status === 'cancelled') {
      const timer = setTimeout(() => {
        handleClose();
      }, 7000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [status, handleClose]);

  if (!status) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        animation: 'slideDown 0.3s ease-out',
        maxWidth: '90%',
        width: '500px',
      }}
      role="alert"
      aria-live="polite"
    >
      <div
        style={{
          background: status === 'success' 
            ? 'linear-gradient(135deg, rgba(0, 255, 0, 0.15) 0%, rgba(0, 200, 0, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(255, 165, 0, 0.15) 0%, rgba(255, 140, 0, 0.1) 100%)',
          backdropFilter: 'blur(8px)',
          border: status === 'success'
            ? '2px solid rgba(0, 255, 0, 0.5)'
            : '2px solid rgba(255, 165, 0, 0.5)',
          borderRadius: '8px',
          padding: '1rem 1.5rem',
          boxShadow: status === 'success'
            ? '0 4px 12px rgba(0, 255, 0, 0.3)'
            : '0 4px 12px rgba(255, 165, 0, 0.3)',
          fontFamily: 'var(--font-family)',
          position: 'relative',
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          aria-label="Close message"
          style={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            background: 'transparent',
            border: 'none',
            color: 'var(--color-fg)',
            fontSize: '1.25rem',
            cursor: 'pointer',
            padding: '0.25rem 0.5rem',
            lineHeight: 1,
          }}
        >
          ×
        </button>

        {status === 'success' && (
          <div>
            <div
              style={{
                fontSize: '1.25rem',
                marginBottom: '0.5rem',
                color: 'rgba(0, 255, 0, 0.95)',
                fontWeight: 'bold',
              }}
            >
              ✅ Purchase Successful!
            </div>
            <div
              style={{
                fontSize: '0.85rem',
                color: 'var(--color-fg)',
                opacity: 0.9,
                marginBottom: '0.5rem',
              }}
            >
              Your credits have been added to your account. Thank you for your purchase!
            </div>
            {sessionId && (
              <div
                style={{
                  fontSize: '0.7rem',
                  color: 'var(--color-fg)',
                  opacity: 0.6,
                  fontFamily: 'monospace',
                }}
              >
                Session: {sessionId.substring(0, 20)}...
              </div>
            )}
          </div>
        )}

        {status === 'cancelled' && (
          <div>
            <div
              style={{
                fontSize: '1.25rem',
                marginBottom: '0.5rem',
                color: 'rgba(255, 165, 0, 0.95)',
                fontWeight: 'bold',
              }}
            >
              ⚠️ Purchase Cancelled
            </div>
            <div
              style={{
                fontSize: '0.85rem',
                color: 'var(--color-fg)',
                opacity: 0.9,
              }}
            >
              Your purchase was cancelled. No charges were made to your account.
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            transform: translateX(-50%) translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
